import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GameController, FlowerLotus, CheckCircle, WarningCircle, ArrowSquareOut } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const ToysTab = () => {
  const [status, setStatus] = useState(null);
  const [modelId, setModelId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/lovense/status`, { withCredentials: true });
      setStatus(res.data);
      setModelId(res.data.model_id || '');
      setEnabled(res.data.enabled !== false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load Lovense status');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/lovense/settings`,
        { model_id: modelId.trim(), enabled },
        { withCredentials: true });
      toast.success('Lovense settings saved');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const testTip = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/api/lovense/test-tip`, {}, { withCredentials: true });
      toast.success('Test tip fired. Your toy should react within 1–2 seconds.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed — must be live first');
    } finally { setTesting(false); }
  };

  if (!status) return <div className="text-slate-500 text-sm">Loading Lovense setup…</div>;

  const ready = status.platform_configured && modelId.trim() && enabled;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="toys-tab">
      {/* Header card */}
      <div className="glass-light rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <FlowerLotus weight="fill" className="w-6 h-6 text-blue-400" />
          <h3 className="font-heading text-xl font-bold text-white">Lovense Toys</h3>
          {ready && (
            <span className="ml-auto px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-bold rounded-full flex items-center gap-1">
              <CheckCircle weight="fill" className="w-3 h-3" /> Ready
            </span>
          )}
          {!status.platform_configured && (
            <span className="ml-auto px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-bold rounded-full flex items-center gap-1">
              <WarningCircle weight="fill" className="w-3 h-3" /> Platform setup pending
            </span>
          )}
        </div>
        <p className="text-slate-400 text-sm">
          Connect your Lovense account so your toys react to tips on stream.
          When viewers tip, your connected toy buzzes automatically — patterns and intensity
          curves are configured inside your <strong className="text-slate-300">Lovense Cam app</strong>.
        </p>
      </div>

      {!status.platform_configured && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-amber-200 text-sm">
          The platform hasn't finished its Lovense developer registration yet. You can save your Model ID now —
          it'll start working the moment Lovense flips us to Approved status.
        </div>
      )}

      {/* Model ID setup */}
      <div className="glass-light rounded-2xl p-6 space-y-4">
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Your Lovense Model ID (mId)</label>
          <Input
            value={modelId}
            onChange={e => setModelId(e.target.value)}
            placeholder="e.g., lustlink_yourname_123"
            className="bg-slate-800 border-white/10 mt-1 font-mono"
            data-testid="lovense-model-id"
          />
          <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
            Find your Model ID in the Lovense Cam app under <strong className="text-slate-400">Settings → Account → Model ID</strong>, or
            <a href="https://www.lovense.com/cam-model/extension" target="_blank" rel="noopener noreferrer"
               className="text-blue-400 hover:underline ml-1 flex items-center gap-0.5">
              get the app <ArrowSquareOut className="w-3 h-3" />
            </a>
          </p>
        </div>

        <label className="flex items-start gap-3 p-3 bg-slate-800/60 hover:bg-slate-800 rounded-xl cursor-pointer border border-white/5">
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-blue-500"
            data-testid="lovense-enabled"
          />
          <div>
            <p className="text-sm font-medium text-white">Enable Lovense on my streams</p>
            <p className="text-xs text-slate-500 mt-0.5">When off, your toy won't react to tips even if your Model ID is set.</p>
          </div>
        </label>

        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
          data-testid="save-lovense-btn">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {/* Test tip */}
      <div className="glass-light rounded-2xl p-6">
        <h4 className="font-heading text-lg font-bold text-white mb-2 flex items-center gap-2">
          <GameController weight="fill" className="w-5 h-5 text-amber-400" />
          Test Your Toy
        </h4>
        <p className="text-slate-400 text-sm mb-4">
          Once you're live, click below to fire a fake tip. Your toy should react in 1–2 seconds.
          If nothing happens, double-check that the Lovense Connect app is running on your broadcasting machine and your toy is paired.
        </p>
        <button
          onClick={testTip}
          disabled={testing || !ready}
          className="px-5 py-2.5 bg-amber-500 text-amber-950 rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50"
          data-testid="lovense-test-tip-btn">
          {testing ? 'Firing…' : 'Fire Test Tip'}
        </button>
        {!ready && (
          <p className="text-xs text-slate-600 mt-2">Test is available when your Model ID is saved and you're live on stream.</p>
        )}
      </div>
    </div>
  );
};

export default ToysTab;
